import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Layout from "../layout/Layout";
import PageStateGuard from "../shared/PageStateGuard";
import BookingDetailsForm from "./BookingDetailsForm";
import CustomerDetailsForm, { type CustomerFormSubmitData, type CustomerFormValues } from "./CustomerDetailsForm";
import BookPreorderStage from "./BookPreorderStage";
import PaymentStep from "./PaymentStep";
import SessionExpiredNotice from "./SessionExpiredNotice";
import SessionRetryNotice from "./SessionRetryNotice";
import BookingConfirmation from "./BookingConfirmation";
import BookingInfoSidebar from "../layout/BookingInfoSiderbar.tsx";
import BookingStepHeader from "./BookingStepHeader";
import BookingInfoInline from "./BookingInfoInline";
import MobileStepNav from "./MobileStepNav";
import type { PreorderCompleteResult } from "../preOrders/PreorderExperience";
import { dietaryRequirementsToCustomerNotes } from "../preOrders/preOrderUtils";
import { PreorderExpiryWarning } from "../preOrders/PreorderExpiryWarning";
import type { BookingTypeGroup } from "../../utils/bookingTypeGroups";
import type {
  PreOrderItem,
  BookingType,
  BookingSubmitRequest,
  BookingOptions,
  CustomerPreOrder,
  PreorderMenu,
  SubmittedAdditionalBooking,
} from "../../../common/jsonRpcTypes";
import { isAbortedRequest, useRequestScreen, useRpcCaller, useWidgetRequests } from "../context/WidgetRequestContext";
import { useInFlight } from "../hooks/useInFlight";
import { usePendingDelay } from "../hooks/usePendingDelay";
import { PendingContent } from "../shared/PendingDots";
import { readPartnerAnalyticsParams } from "../../analytics";
import { useAnalyticsConfiguration, useFunnelAnalytics } from "../hooks/useAnalytics";
import { useBookingSteps, getStageVisibility, getStepIndicatorInfo } from "../hooks/useBookingSteps";
import { useFormattedBookingInfo } from "../hooks/useFormattedBookingInfo";
import { useWidgetLocale } from "../hooks/useWidgetLocale";
import { redirectToOldGuestExperience } from "../../utils/redirectToOldExperience";
import { resolveVenueImageUrl } from "../../utils/venueImage";
import { notifyBugsnag } from "../../bugsnag";
import { isDmnHeaderSource } from "../../utils/bookingSource";
import { useGuestCheckoutSettings, type GuestCheckoutSettings } from "../context/GuestCheckoutSettingsContext";
import { useWidgetScroll } from "../context/WidgetScrollContext";
import { useWidgetTopBar } from "../context/WidgetTopBarContext";
import BookingBackButton from "./BookingBackButton";
import { useVenueList } from "../hooks/useVenueList";
import { useWidgetAutoExpandForPreorder } from "../hooks/useWidgetAutoExpandForPreorder";

/** Data shape passed to parent when preorder selections change. */
export interface PreorderSelections {
  items: { menuId: string; itemId: string; name: string; quantity: number; price: number }[];
  /** Full PreOrderItem objects for the submit request. */
  rawItems: PreOrderItem[];
  subtotal: number;
  serviceChargePercentage: number;
  serviceChargeAmount: number;
  total: number;
  /** Required payment amount based on payment percentage (from backend). */
  requiredPaymentAmount: number;
  /** Required payment amount including tax for tax-exclusive venues (from backend). */
  requiredPaymentGross?: number;
  /** Pending preorder ID assigned by the API when items were saved. */
  pendingPreorderId: string | undefined;
  /** Whether the preorder is tax-inclusive (false = tax-exclusive). */
  taxInclusive?: boolean;
  /** Tax label from API (e.g. "VAT", "Sales Tax"). */
  taxLabel?: string;
  /** Gross total including tax (for tax-exclusive venues). */
  totalGross?: number;
  /** Tax breakdown entries (for tax-exclusive venues). */
  taxBreakdown?: { label: string; amount: number }[];
  /**
   * The selected booking type's pre-order payment requirement
   * (`customer_preorder_settings.payment_required`). An empty string means the
   * pre-order is pay-at-venue; a non-empty value means the lead booker must pay.
   */
  preorderPaymentRequired?: string | null;
  /** Per-booking-type breakdown, populated only for linked bookings. */
  bookingTypeGroups?: BookingTypeGroup[];
  /**
   * Dietary requirements captured in the dietary modal. Carried so the shared
   * order note can be submitted as `customer_notes` and so the requirements
   * survive stepping back into the pre-order stage.
   */
  dietaryRequirements?: CustomerPreOrder["dietaryRequirements"];
}
import { useBookingCreate } from "../hooks/useBookingCreate";
import { useUrlParamSeeding } from "../hooks/useUrlParamSeeding";
import type { AppliedVoucher } from "../hooks/useVoucherRedemption";
import * as z from "zod/v4";
import { parseDurationFromUrl } from "../../utils/durationUtils";
import { saveVouchersToUrl, loadVouchersFromUrl } from "../../utils/voucherUrlUtils";
import { withoutDietaryNotes } from "../../../common/dietaryRequirements";
import { getSafeReturnUrl, parseBookingPrefill } from "./bookingUrlParams";
import { partnerReturnBinding } from "../../utils/returnUrlRedirect";
/**
 * The stages of the booking journey.
 *
 * - "initial"           – Date, time, covers, booking type selection
 * - "details"           – Customer personal details
 * - "preorders"         – Optional food/drink pre-ordering
 * - "payment"           – Deposit or preorder payment
 * - "deposit"           – Deposit-only payment (when no preorder payment)
 * - "complete"          – Confirmation page
 * - "complete-redirect" – Auto-redirect to partner return_url
 */
export type BookingStage =
  | "initial"
  | "details"
  | "preorders"
  | "payment"
  | "deposit"
  | "complete"
  | "complete-redirect";

const historyStateSchema = z.object({
  stage: z.enum(["initial", "details", "preorders", "payment", "deposit", "complete", "complete-redirect"]),
  isEnquiry: z.boolean().optional(),
});

/** Shape of the pending booking returned by the API. */
export interface PendingBooking {
  _id: string;
  venue_id: string;
  date: string;
  time: string;
  num_people: number;
  duration: number;
  type: { id: string; name: string };
  is_pending: boolean;
}

/** Booking rules per weekday. */
export interface BookingRules {
  bookings_from: string;
  bookings_to: string;
  min_people: number;
  max_people: number;
  min_duration: number;
  max_duration: number;
  booking_available: boolean;
  bookings_shut: string;
}

/** Re-export the zod-inferred BookingType so consumers use a single definition. */
export type { BookingType };

/** Venue configuration from the create-pending-booking response. */
export interface VenueConfig {
  venueId: string;
  title: string;
  currency: string;
  bookingTypes: BookingType[];
  openingTimes: Record<string, { open: string; close: string; status: boolean }>;
  venueBookingUnavailable: Record<string, unknown[]>;
  paymentsEnabled: boolean;
  authsEnabled: boolean;
  corporateCardFee: number;
  manage_own_bookings: boolean;
  marketing_default_opt_in: boolean;
  bookingTags: BookingTag[];
  customPaymentMessages: string | null;
  marketing_preferences?: MarketingPreference[];
  venueGroupHasSodaIntegration?: boolean;
  enableApplePay?: boolean | string;
  enableGooglePay?: boolean | string;
  enableMobilePaymentMethods?: boolean;
  collins_features?: Record<string, unknown>;
}

/** A tag that can be applied to bookings. */
export interface BookingTag {
  id: string;
  label: string;
  type: "select" | "multiselect" | "text" | "boolean";
  options?: string[];
}

/** Marketing preference option. */
export interface MarketingPreference {
  id: string;
  label: string;
  description: string;
}

/** Availability response from the API. */
export interface Availability {
  action: "accept" | "reject" | "suggest" | null;
  data: {
    alternative_times: AlternativeTime[];
    alternative_venue_times: AlternativeVenueTime[];
  };
}

/** An alternative time suggestion. */
export interface AlternativeTime {
  time: string;
  date: string;
}

/** An alternative venue/time suggestion. */
export interface AlternativeVenueTime {
  venue_id: string;
  venue_name: string;
  time: string;
  date: string;
}

/** Deposit requirement information. */
export interface DepositRequired {
  total: number;
  totalFormatted: string;
  type: "payment" | "authenticate" | "auth";
  takeWithPreorder: boolean;
}

export type {
  BookingOptions,
  PreorderMenuItem,
  PreorderMenu,
} from "../../../common/jsonRpcTypes";

/**
 * Utility functions for persisting vouchers in URL parameters
 * to survive Stripe Connect redirects.
 */

/** Data collected for a preorder submission. */
export interface PreorderData {
  items: unknown[];
  customer_notes: string;
  serviceChargePercentage: number;
  serviceChargeAmount: number;
  dietaryRequirements: unknown[];
}

/** Full state of the booking form. */
export interface BookingFormState {
  stage: BookingStage;
  pendingBookingId: string | null;
  accessToken: string | null;
  booking: PendingBooking | null;
  venueConfig: VenueConfig | null;
  availability: Availability | null;
  bookingOptions: BookingOptions | null;
  preorderMenus: PreorderMenu[];
  preorder: Record<string, PreorderData>;
  customerDetails: CustomerDetails | null;
  errors: string[] | null;
  loading: boolean;
  submitting: boolean;
}

/** Customer details collected in Step 4. */
export interface CustomerDetails {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  notes: string;
  dob: string;
  newsletter_signup: boolean;
  /** No dedicated checkbox collects this; kept optional so it's omitted (not sent as false) when absent. */
  partner_data_permission?: boolean;
  marketing_preferences: Record<string, boolean>;
  policy_confirm: boolean;
  tags: unknown[];
  guest_names: string[];
}

/** Confirmation data returned after booking submission. */
export interface BookingConfirmationData {
  booking: {
    _id: string;
    reference: string;
    venue_id: string;
    date: string;
    time: string;
    num_people: number;
    type: { id: string; name: string };
    first_name: string;
    last_name: string;
    email: string;
    deposits: { amount: number; type: string; payment_ref: string }[];
  };
  bookingStatus: "confirmed" | "received";
  /** Bookings created alongside the primary one when this is a linked booking. */
  additionalBookings: SubmittedAdditionalBooking[];
  venue: {
    _id: string;
    title: string;
    path: string;
    description: string;
    currency: string;
    country_code?: string;
  };
}

/** Zod schema for validating /book route query parameters. */
const bookingQueryParamsSchema = z.object({
  venue_id: z.string().default(""),
  source: z.string().default("partner"),
  type: z.string().optional(),
  date: z.string().optional(),
  time: z.string().optional(),
  duration: z.string().optional(),
  num_people: z.coerce.number().int().positive().optional(),
  return_url: z.string().optional(),
  return_method: z.string().optional(),
  fixed_type: z.string().optional(),
  affiliate_id: z.string().optional(),
  offer: z.string().optional(),
  locale: z.string().optional(),
  additionalTypes: z.string().optional(),
  venue_group: z.string().optional(),
  hide_offers: z.string().optional(),
  search_venues: z.string().optional(),
  show_type_first: z.string().optional(),
  ga_account: z.string().optional(),
  gtm_account: z.string().optional(),
});

/** Query parameters accepted by the /book route. */
type BookingQueryParams = z.infer<typeof bookingQueryParamsSchema>;

/** Parsed query parameters with normalised duration and boolean settings. */
interface ParsedBookingQueryParams
  extends Omit<BookingQueryParams, "duration" | "hide_offers" | "search_venues" | "show_type_first"> {
  duration: number | null | undefined;
  hide_offers: boolean;
  search_venues: boolean;
  show_type_first: boolean;
  /** All venue IDs (from URL param and/or web component attribute), split from comma-separated string. */
  venue_ids: string[];
}

/** Guest count the widget starts on, clamped into the booking type's min/max people range. */
const WIDGET_PRESET_COVERS = 2;

const SUPPRESSED_INITIAL_FIELD_ERROR_CODES = new Set([
  "missing_type",
  "missing_num_people",
  "missing_date",
  "missing_time",
  "missing_duration",
  "duration_missing",
]);

/**
 * Flatten API validation errors from an errors map into a deduplicated list
 * of messages, suppressing expected initial-field errors that appear before
 * the user has entered anything.
 *
 * Extracted from BookingForm to keep the component's cognitive complexity low.
 */
function getValidationErrorMessages(errors: Record<string, string[]> | null): string[] {
  if (!errors) return [];
  const messages: string[] = [];
  const seenMessages = new Set<string>();
  for (const [errorCode, fieldMessages] of Object.entries(errors)) {
    if (SUPPRESSED_INITIAL_FIELD_ERROR_CODES.has(errorCode)) continue;
    if (Array.isArray(fieldMessages)) {
      for (const msg of fieldMessages) {
        if (seenMessages.has(msg)) continue;
        seenMessages.add(msg);
        messages.push(msg);
      }
    }
  }
  return messages;
}

function parseBooleanSearchParam(param: string | null, defaultValue: boolean): boolean {
  if (param === null || param === "" || param === "undefined" || param === "null") {
    return defaultValue;
  }
  return param !== "false";
}

/**
 * The locale sent to Collins: the full app's `?locale=` or `locale` setting.
 * The widget sends none, so Collins uses its default.
 */
function getBackendLocale(searchParams: URLSearchParams, settings: GuestCheckoutSettings): string | undefined {
  if (settings.isWidget) return undefined;
  return searchParams.get("locale") ?? settings.locale ?? undefined;
}

function getQueryParams(searchParams: URLSearchParams, settings: GuestCheckoutSettings): ParsedBookingQueryParams {
  const raw: Record<string, string | undefined> = {
    venue_id: searchParams.get("venue_id") ?? undefined,
    source: searchParams.get("source") ?? undefined,
    type: searchParams.get("type") ?? undefined,
    date: searchParams.get("date") ?? undefined,
    time: searchParams.get("time") ?? undefined,
    duration: searchParams.get("duration") ?? undefined,
    num_people: searchParams.get("num_people") ?? undefined,
    return_url: searchParams.get("return_url") ?? undefined,
    return_method: searchParams.get("return_method") ?? undefined,
    fixed_type: searchParams.get("fixed_type") ?? undefined,
    affiliate_id: searchParams.get("affiliate_id") ?? undefined,
    offer: searchParams.get("offer") ?? undefined,
    locale: getBackendLocale(searchParams, settings),
    additionalTypes: searchParams.get("additionalTypes") ?? undefined,
    venue_group: searchParams.get("venue_group") ?? undefined,
    hide_offers: searchParams.get("hide_offers") ?? undefined,
    search_venues: searchParams.get("search_venues") ?? undefined,
    show_type_first: searchParams.get("show_type_first") ?? undefined,
    ga_account: searchParams.get("ga_account") ?? undefined,
    gtm_account: searchParams.get("gtm_account") ?? undefined,
  };

  const result = bookingQueryParamsSchema.safeParse(raw);
  const params: BookingQueryParams = result.success
    ? {
        ...result.data,
        return_url: getSafeReturnUrl(result.data.return_url),
        return_method: result.data.return_method?.toLowerCase(),
      }
    : {
        venue_id: searchParams.get("venue_id") ?? "",
        source: searchParams.get("source") ?? "partner",
        type: searchParams.get("type") ?? undefined,
        date: searchParams.get("date") ?? undefined,
        time: searchParams.get("time") ?? undefined,
        duration: undefined,
        num_people: undefined,
        return_url: getSafeReturnUrl(searchParams.get("return_url") ?? undefined),
        return_method: searchParams.get("return_method")?.toLowerCase() ?? undefined,
        fixed_type: searchParams.get("fixed_type") ?? undefined,
        affiliate_id: searchParams.get("affiliate_id") ?? undefined,
        offer: searchParams.get("offer") ?? undefined,
        locale: getBackendLocale(searchParams, settings),
        additionalTypes: searchParams.get("additionalTypes") ?? undefined,
        venue_group: searchParams.get("venue_group") ?? undefined,
        hide_offers: undefined,
        search_venues: undefined,
        show_type_first: undefined,
        ga_account: undefined,
        gtm_account: undefined,
      };

  // Normalise duration: sanitise, convert hours→minutes, handle negatives
  const normalisedDuration = parseDurationFromUrl(params.duration, params.time) ?? undefined;

  // Derive venue_ids from the URL param and/or the web component `venues` attribute.
  // The `venues` attribute (via settings) takes precedence when provided.
  const venueSource = settings.venues ?? params.venue_id;
  const venueIds = venueSource
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

  return {
    ...params,
    // Keep venue_id as the first venue for backward compatibility
    venue_id: venueIds[0] ?? params.venue_id,
    venue_ids: venueIds,
    duration: normalisedDuration,
    hide_offers: parseBooleanSearchParam(params.hide_offers ?? null, settings.hideOffers),
    search_venues: parseBooleanSearchParam(params.search_venues ?? null, settings.searchVenues),
    show_type_first: parseBooleanSearchParam(params.show_type_first ?? null, settings.showTypeFirst),
  };
}

/** Transform tags from Record<id, value> to the array-of-objects format expected by Collins API. */
function transformTagsToArray(
  formTags: Record<string, string | string[] | boolean>,
  bookingTags: readonly { id: string; label: string; type: string }[] | undefined,
): { id: string; value: string | string[] | boolean | null; label: string; type: string }[] {
  if (!bookingTags) return [];
  return Object.entries(formTags).reduce<
    { id: string; value: string | string[] | boolean | null; label: string; type: string }[]
  >((acc, [tagId, tagValue]) => {
    const tagConfig = bookingTags.find((t) => t.id === tagId);
    if (tagConfig) {
      acc.push({
        id: tagId,
        value: tagValue,
        label: tagConfig.label,
        type: tagConfig.type,
      });
    }
    return acc;
  }, []);
}

/**
 * Map the current booking stage to a screen-reader announcement translation key.
 * Extracted from BookingForm to keep the component's cognitive complexity low.
 */
function getStageAnnouncement(
  stage: string | undefined,
  isEnquiry: boolean,
  t: (key: string) => string,
): string | undefined {
  switch (stage) {
    case "initial":
      return t("booking_stage_initial");
    case "details":
      return t(isEnquiry ? "enquiry_stage_details" : "booking_stage_details");
    case "preorders":
      return t("booking_stage_preorders");
    case "payment":
    case "deposit":
      return t("booking_stage_payment");
    case "complete":
    case "complete-redirect":
      return t(isEnquiry ? "enquiry_stage_complete" : "booking_stage_complete");
    default:
      return undefined;
  }
}

/**
 * Build the PreorderSelections object from a completed pre-order result.
 *
 * Extracted to module scope to keep the pre-order completion handler's function
 * nesting shallow (avoids the deep `map` callback nesting flagged by S2004).
 */
export function buildPreorderSelections(result: PreorderCompleteResult): PreorderSelections {
  return {
    items: result.items.map((item) => ({
      menuId: item.menu_id,
      itemId: item.id,
      name: item.name ?? "",
      quantity: item.quantity,
      price: Number(item.price) || 0,
    })),
    rawItems: result.items,
    subtotal: result.totalCost,
    serviceChargePercentage: result.serviceChargePercentage,
    serviceChargeAmount: result.serviceChargeAmount,
    total: result.totalCostWithService,
    requiredPaymentAmount: result.requiredPaymentAmount ?? result.totalCostWithService,
    requiredPaymentGross: result.requiredPaymentGross,
    pendingPreorderId: result.pendingPreorderId,
    taxInclusive: result.taxInclusive,
    taxLabel: result.taxLabel,
    totalGross: result.totalGross,
    taxBreakdown: result.taxBreakdown,
    bookingTypeGroups: result.bookingTypeGroups,
    dietaryRequirements: result.dietaryRequirements,
  };
}

/**
 * BookingForm — top-level orchestrator for the booking creation journey.
 *
 * Manages a multi-step state machine that walks the customer through:
 * 1. Date / time / covers selection
 * 2. Customer details
 * 3. Pre-orders (optional)
 * 4. Payment (optional)
 * 5. Confirmation
 *
 * State is managed by the `useBookingCreate` hook which handles API
 * calls, validation, and availability checking via a `useReducer`.
 */
/**
 * Blocks forward navigation when the pending-booking session cannot be verified.
 * "expired" is terminal (genuine session expiry — restart required); "transient"
 * is a recoverable network/upstream failure that can be retried via `proceed`.
 */
type FlowBlock =
  | { kind: "expired"; message: string | null }
  | { kind: "transient"; message: string | null; proceed: () => void | Promise<void> };

function shouldRedirectToOldExperience(venueConfig: { collins_features?: unknown } | null): boolean {
  if (!venueConfig) return false;
  const collinsFeatures = venueConfig.collins_features;
  return (
    !!collinsFeatures &&
    typeof collinsFeatures === "object" &&
    !Array.isArray(collinsFeatures) &&
    (collinsFeatures as Record<string, unknown>).redirect_old_booking_experience === true
  );
}

function useOldExperienceRedirect(venueConfig: { collins_features?: unknown } | null, venueId: string): boolean {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hasAttemptedRef = useRef(false);

  useEffect(() => {
    if (hasAttemptedRef.current || !shouldRedirectToOldExperience(venueConfig)) return;

    hasAttemptedRef.current = true;
    setIsRedirecting(true);
    redirectToOldGuestExperience()
      .then((redirected) => {
        if (!redirected) setIsRedirecting(false);
      })
      .catch((error) => {
        notifyBugsnag(new Error("Failed to redirect to old booking experience"), {
          component: "BookingForm",
          venueId,
          timestamp: new Date().toISOString(),
          error,
        });
        setIsRedirecting(false);
      });
  }, [venueConfig, venueId]);

  return isRedirecting;
}

interface BookingErrorBannersProps {
  readonly error: string | null;
  readonly submitError: string | null;
  readonly submitUnconfirmed: boolean;
  readonly paymentUnconfirmed: boolean;
  readonly createBookingFailed: boolean;
  readonly validationErrorMessages: string[];
  readonly stage: ReturnType<typeof useBookingCreate>["state"]["stage"];
  readonly errorBannerRef: React.RefObject<HTMLDivElement | null>;
  readonly retryCreateBooking: () => void | Promise<void>;
  readonly retrySubmit: () => void | Promise<void>;
  readonly t: TFunction;
}

function BookingErrorBanners({
  error,
  submitError,
  submitUnconfirmed,
  paymentUnconfirmed,
  createBookingFailed,
  validationErrorMessages,
  stage,
  errorBannerRef,
  retryCreateBooking,
  retrySubmit,
  t,
}: BookingErrorBannersProps) {
  const retry = useInFlight();
  const showRetryDots = usePendingDelay(retry.isPending);
  return (
    <>
      {error && (
        <div
          ref={errorBannerRef}
          className="gcw:flex gcw:flex-col gcw:gap-2 gcw:rounded-md gcw:border gcw:border-error gcw:bg-error/10 gcw:p-bs-3 gcw:text-error widget-error-panel gcw:mb-bs-3"
          role="alert"
          tabIndex={-1}
        >
          <p>{error}</p>
          {createBookingFailed && (
            <button
              type="button"
              className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:self-start gcw:rounded-2xl gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2"
              onClick={() => void retry.run(async () => retryCreateBooking())}
              disabled={retry.isPending}
              aria-busy={retry.isPending || undefined}
            >
              <PendingContent showDots={showRetryDots}>{t("booking_try_again", "Try Again")}</PendingContent>
            </button>
          )}
        </div>
      )}
      {submitError && (
        <div
          ref={errorBannerRef}
          className="gcw:flex gcw:flex-col gcw:gap-2 gcw:rounded-md gcw:border gcw:border-error gcw:bg-error/10 gcw:p-bs-3 gcw:text-error widget-error-panel gcw:mb-bs-3"
          role="alert"
          tabIndex={-1}
        >
          <p>{submitError}</p>
          <button
            type="button"
            className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:self-start gcw:rounded-2xl gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2"
            onClick={() => void retry.run(async () => retrySubmit())}
            disabled={retry.isPending}
            aria-busy={retry.isPending || undefined}
          >
            <PendingContent showDots={showRetryDots}>{t("booking_try_again", "Try Again")}</PendingContent>
          </button>
        </div>
      )}
      {submitUnconfirmed && (
        <div
          ref={errorBannerRef}
          className="gcw:rounded-md gcw:border gcw:border-error gcw:bg-error/10 gcw:p-bs-3 gcw:text-error widget-error-panel gcw:mb-bs-3"
          role="alert"
          tabIndex={-1}
        >
          <p>
            {t(
              "booking_unconfirmed_message",
              "We couldn't confirm your booking. Please check your email for a booking confirmation before trying again. If you haven't received one within 10 minutes, please get in touch with the venue directly.",
            )}
          </p>
        </div>
      )}
      {paymentUnconfirmed && !submitUnconfirmed && (
        <div
          ref={errorBannerRef}
          className="gcw:rounded-md gcw:border gcw:border-error gcw:bg-error/10 gcw:p-bs-3 gcw:text-error widget-error-panel gcw:mb-bs-3"
          role="alert"
          tabIndex={-1}
        >
          <p>
            {t(
              "payment_timeout_message",
              "We are still confirming your payment. Please do not pay again as this could result in a duplicate charge. Check your email for a confirmation and if nothing arrives within 10 minutes, get in touch with the venue directly.",
            )}
          </p>
        </div>
      )}
      {!error && validationErrorMessages.length > 0 && stage === "initial" && (
        <output
          /* A heads-up, not a failure: the guest has fields left to complete.
             The amber utilities state that, so the design marker has to agree —
             `widget-error-panel` painted it red on a themed surface. */
          className="gcw:rounded-md gcw:border gcw:border-warning gcw:bg-amber-50 gcw:p-bs-3 gcw:text-amber-800 widget-warning-panel gcw:mb-bs-3 gcw:block"
          aria-live="polite"
        >
          <ul className="gcw:list-disc gcw:pl-4 gcw:space-y-1">
            {validationErrorMessages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </output>
      )}
    </>
  );
}

interface BookingNavigationProps {
  readonly showStepHeader: boolean;
  readonly widgetTopBarSlot: HTMLElement | null;
  readonly currentStage: ReturnType<typeof useBookingCreate>["state"]["stage"];
  readonly bookingSteps: ReturnType<typeof useBookingSteps>["bookingSteps"];
  readonly onBack: () => void;
  readonly backDisabled: boolean;
  /**
   * The venue's name, shown on the header's left only once the sidebar that
   * normally carries it has gone.
   */
  readonly venueName?: string;
  /** Whether the venue sidebar is on screen, which states the name already. */
  readonly showSidebar: boolean;
  readonly t: TFunction;
}

function BookingNavigation({
  showStepHeader,
  widgetTopBarSlot,
  currentStage,
  bookingSteps,
  onBack,
  backDisabled,
  venueName,
  showSidebar,
  t,
}: BookingNavigationProps) {
  if (!showStepHeader) return null;
  if (widgetTopBarSlot) return <BookingBackButton onClick={onBack} disabled={backDisabled} t={t} />;
  return (
    <div id="gc-booking-step-navigation-desktop" className="gcw:hidden gcw:lg:block">
      {/* The page header shows the DMN logo, so this row carries the Back
          control and step indicator — and, from the pre-order stage on, the
          venue name too. Up to Your Details the sidebar states the name, so
          the header leaves it out rather than repeating it; past that the
          sidebar is gone and BookingInfoInline's copy of the name is
          `lg:hidden`, which left desktop with no venue on screen at all. */}
      <BookingStepHeader
        venueName={venueName}
        showBranding={!showSidebar}
        currentStage={currentStage}
        steps={bookingSteps}
        onBack={onBack}
        backDisabled={backDisabled}
      />
    </div>
  );
}

interface GuardedStageContentProps {
  readonly flowBlock: FlowBlock | null;
  readonly runWithSessionGuard: (proceed: () => void | Promise<void>) => Promise<void>;
  readonly children: React.ReactNode;
}

function GuardedStageContent({ flowBlock, runWithSessionGuard, children }: GuardedStageContentProps) {
  if (flowBlock?.kind === "expired") {
    return <SessionExpiredNotice onRestart={() => window.location.reload()} />;
  }
  return (
    <>
      {flowBlock?.kind === "transient" && (
        <SessionRetryNotice message={flowBlock.message} onRetry={() => runWithSessionGuard(flowBlock.proceed)} />
      )}
      {children}
    </>
  );
}

interface PreorderExpiryMonitorProps {
  readonly stage: string;
  readonly preorderSelections: PreorderSelections | null;
  readonly preorderCacheExpired: boolean;
  readonly pendingBookingId: string | null;
  readonly resyncKey: number;
  readonly onExpired: () => void;
}

/**
 * BookingForm-level cache expiry monitor — stays alive across preorder and
 * payment stages so expiry is detected even when PreorderExperience is
 * unmounted. Only active when there are confirmed preorder selections to
 * protect; the inner PreorderExperience has its own instance for the
 * preorder-stage UI (banner, basket clearing).
 */
function PreorderExpiryMonitor({
  stage,
  preorderSelections,
  preorderCacheExpired,
  pendingBookingId,
  resyncKey,
  onExpired,
}: PreorderExpiryMonitorProps) {
  const monitorsStage = stage === "preorders" || stage === "payment";
  const itemCount = preorderSelections?.rawItems.length ?? 0;
  if (!monitorsStage || preorderCacheExpired || itemCount === 0) return null;
  return (
    <PreorderExpiryWarning
      bookingId={pendingBookingId ?? undefined}
      active={itemCount > 0}
      onExpired={onExpired}
      resyncKey={resyncKey}
      expireWhenInactive
    />
  );
}

const byId = (option: { id: string }): string => option.id;
const byUnderscoreId = (option: { _id: string }): string => option._id;

/**
 * Keep a guest's selection (offer, package) in sync with the option list the
 * API returns: preserved while still present, cleared when the list changed to
 * one without it (e.g. the guest switched to a booking type the selection is
 * not available for).
 */
function usePreservedSelection<T>(
  options: T[],
  setSelected: React.Dispatch<React.SetStateAction<string | null>>,
  getId: (option: T) => string,
): void {
  const previousRef = useRef(options);
  useEffect(() => {
    if (previousRef.current === options) return;
    const previousOptions = previousRef.current;
    previousRef.current = options;
    setSelected((prev) => {
      if (!prev) return null;
      if (options.length > 0) {
        return options.some((option) => getId(option) === prev) ? prev : null;
      }
      // Empty new list: preserve if the previous one was also empty (initial
      // load), clear if it had options (guest changed to a type without them).
      return previousOptions.length > 0 ? null : prev;
    });
  }, [options, setSelected, getId]);
}

const additionalTypeEntrySchema = z.union([z.string(), z.object({ id: z.string() })]);

/**
 * Ids of the linked booking types declared in `additionalTypes`. Collins sends
 * either a plain id or an object carrying `id`.
 */
function linkedBookingTypeIds(additionalTypes: unknown): string[] {
  const parsed = z.array(additionalTypeEntrySchema).safeParse(additionalTypes ?? []);
  if (!parsed.success) return [];
  return parsed.data.map((entry) => (typeof entry === "string" ? entry : entry.id));
}

/** Venue booking types that are part of a linked (multi-type) booking. */
function findLinkedBookingTypes(
  bookingOptions: BookingOptions | null,
  bookingTypes: BookingType[] | undefined,
): BookingType[] {
  const linkedIds = linkedBookingTypeIds(bookingOptions?.additionalTypes);
  if (linkedIds.length === 0) return [];
  return (bookingTypes ?? []).filter((bookingType) => linkedIds.includes(bookingType.id));
}

const BookingForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const settings = useGuestCheckoutSettings();
  const prefill = useMemo(() => parseBookingPrefill(searchParams), [searchParams]);
  const scrollWidgetToScreenTop = useWidgetScroll();
  const widgetTopBarSlot = useWidgetTopBar();
  const requests = useWidgetRequests();
  const call = useRpcCaller();
  const abortScreen = requests?.abortScreen;
  // Aborting a booking or payment request would not undo it, so Back waits for it instead.
  const isBackLocked = requests?.isCommitInFlight ?? false;
  const [stageHeadingSlot, setStageHeadingSlot] = useState<HTMLElement | null>(null);
  // Memoise so `queryParams.venue_ids` (built via split/map/filter) keeps a
  // stable reference across renders. Without this, `useVenueList` receives a new
  // array each render, re-runs its fetch effect, and loops forever for multi-venue.
  const queryParams = useMemo(() => getQueryParams(searchParams, settings), [searchParams, settings]);

  const isMultiVenue = queryParams.venue_ids.length > 1;

  // For multi-venue, the selected venue starts as the first ID and can be changed
  // by the venue picker. For single-venue, it's always the URL param.
  const [selectedVenueId, setSelectedVenueId] = useState<string>(queryParams.venue_ids[0] ?? "");

  // Fetch venue summaries when multiple venue IDs are provided
  const {
    venues: venueList,
    loading: venueListLoading,
    error: venueListError,
    retry: retryVenueList,
  } = useVenueList(queryParams.venue_ids, queryParams.source);

  const [selectedOffer, setSelectedOffer] = useState<string | null>(() =>
    queryParams.hide_offers ? null : (queryParams.offer ?? null),
  );
  const [lastSubmitData, setLastSubmitData] = useState<BookingSubmitRequest | null>(null);
  const [savedCustomerData, setSavedCustomerData] = useState<CustomerFormSubmitData | null>(null);
  const [savedCustomerFormValues, setSavedCustomerFormValues] = useState<CustomerFormValues | null>(null);

  /**
   * The offer every `/createBooking` update should write to the pending booking.
   *
   * `selectedOffer` is seeded from `?offer=` but is then owned by the guest, so
   * it — not the immutable URL value — is what later updates must carry. Sending
   * the URL offer instead would silently restore it whenever the guest changed
   * their booking details after declining or swapping the offer, and the payment
   * would then be taken against an offer they had already rejected.
   *
   * An empty string is how Collins clears a stored offer. It is only sent when
   * the URL put one there to begin with, so a booking that never had an offer
   * does not carry the field at all. Clearing an offer the guest picked in the
   * form is handled by `persistOfferToPendingBooking` on the details step.
   */
  const currentOffer = useMemo(
    () => selectedOffer ?? (queryParams.offer ? "" : undefined),
    [selectedOffer, queryParams.offer],
  );

  const {
    state,
    updateBooking: updateBookingWithOffer,
    persistOfferToPendingBooking,
    updateCoreDetails,
    submitBooking,
    clearPendingBookingUser,
    retryCreateBooking,
    clearSubmitError,
    clearValidationErrors,
    clearError,
    setStage,
    setIsEnquiry,
    setPaymentUnconfirmed,
    resetForVenueChange,
  } = useBookingCreate(selectedVenueId, queryParams.source, {
    affiliateId: queryParams.affiliate_id,
    offer: queryParams.offer,
    locale: queryParams.locale,
    additionalTypes: queryParams.additionalTypes,
    venueGroup: queryParams.venue_group,
    type: queryParams.type,
    num_people: queryParams.num_people,
    date: queryParams.date,
    time: queryParams.time,
    duration: queryParams.duration ?? undefined,
  });

  const hasSelectedTimeRef = useRef(state.coreBookingDetails.time !== null);
  const hasSelectedDurationRef = useRef(state.coreBookingDetails.duration !== null);
  if (state.coreBookingDetails.time !== null) hasSelectedTimeRef.current = true;
  if (state.coreBookingDetails.duration !== null) hasSelectedDurationRef.current = true;

  const initialTime = state.coreBookingDetails.time ?? (hasSelectedTimeRef.current ? undefined : queryParams.time);
  const initialDuration =
    state.coreBookingDetails.duration ??
    (hasSelectedDurationRef.current ? undefined : (queryParams.duration ?? undefined));

  const widgetLocale = useWidgetLocale(state.countryConfig?.country_code);
  useEffect(() => {
    if (widgetLocale && i18n.isInitialized) void i18n.changeLanguage(widgetLocale);
  }, [i18n, widgetLocale]);

  /**
   * Whether returning to the first stage should resume on its last sub-step
   * ("Select Your Time") rather than restart on its first one.
   */
  const [resumeInitialAtTime, setResumeInitialAtTime] = useState(false);
  const [hasReachedLaterStage, setHasReachedLaterStage] = useState(state.stage !== "initial");

  /** Handle venue selection from the multi-venue picker. */
  const handleVenueSelect = useCallback(
    (venueId: string) => {
      if (venueId === selectedVenueId) return;
      resetForVenueChange();
      setSavedCustomerFormValues(null);
      setSavedCustomerData(null);
      setLastSubmitData(null);
      // A new venue clears the booking details, so there is no sub-step left to
      // resume on: the first stage starts afresh from here on.
      setResumeInitialAtTime(false);
      setHasReachedLaterStage(false);
      setSelectedVenueId(venueId);
      // Update venue_id in URL and clear booking-detail params that were specific
      // to the previous venue. Without this, the old date / time / covers / type /
      // duration survive in the URL and get re-seeded into the new venue's form
      // (via queryParams fallback in BookingDetailsForm props and the initial-load
      // effect in useBookingCreate).
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("venue_id", venueId);
          next.delete("date");
          next.delete("time");
          next.delete("num_people");
          next.delete("type");
          next.delete("duration");
          next.delete("offer");
          return next;
        },
        { replace: true },
      );
    },
    [selectedVenueId, resetForVenueChange, setSearchParams],
  );

  /**
   * Every pending-booking update re-sends the offer, so it must carry the
   * guest's current choice rather than the `?offer=` param.
   *
   * The choice is passed per call instead of as a hook option because the
   * hook's initial-load effect is keyed on that option: feeding it a changing
   * value would restart the journey on every offer change, minting a new
   * pending booking seeded from the URL and discarding the details the guest
   * had already chosen.
   */
  const updateBooking = useCallback(
    (details: Parameters<typeof updateBookingWithOffer>[0]) => updateBookingWithOffer(details, currentOffer),
    [updateBookingWithOffer, currentOffer],
  );

  const analyticsVenue = useMemo(
    () =>
      state.venueConfig
        ? { id: state.venueConfig.venueId, title: state.venueConfig.title, currency: state.venueConfig.currency }
        : undefined,
    [state.venueConfig],
  );

  // Read from the URL rather than from `queryParams`, whose `source` defaults to
  // `partner` for booking creation: an unattributed journey is ours to measure.
  useAnalyticsConfiguration({
    ...readPartnerAnalyticsParams(),
    venue: analyticsVenue,
  });

  // `widget_loaded`, the `select_*` steps and `begin_checkout`.
  useFunnelAnalytics({
    venue: analyticsVenue,
    bookingTypeId: state.coreBookingDetails.type,
    bookingTypeName: state.venueConfig?.bookingTypes.find((bt) => bt.id === state.coreBookingDetails.type)?.name,
    partySize: state.coreBookingDetails.num_people,
    date: state.coreBookingDetails.date,
    time: state.coreBookingDetails.time,
    pendingBookingId: state.pendingBookingId,
    hasReachedDetails: state.stage !== "initial",
  });

  const errorBannerRef = useRef<HTMLDivElement>(null);

  const [confirmationData, setConfirmationData] = useState<BookingConfirmationData | null>(null);
  const [preorderSelections, setPreorderSelections] = useState<PreorderSelections | null>(null);
  const [preorderCacheExpired, setPreorderCacheExpired] = useState(false);
  const [preorderExpiryResyncKey, setPreorderExpiryResyncKey] = useState(0);

  // Initialize voucher state from URL parameters to survive Stripe Connect redirects
  const [appliedVouchersState, setAppliedVouchersState] = useState<AppliedVoucher[]>(() => loadVouchersFromUrl());

  // Wrapper to update voucher state and persist to URL
  const setAppliedVouchers = useCallback(
    (vouchers: AppliedVoucher[] | ((prev: AppliedVoucher[]) => AppliedVoucher[])) => {
      const newVouchers = typeof vouchers === "function" ? vouchers(appliedVouchersState) : vouchers;
      setAppliedVouchersState(newVouchers);
      saveVouchersToUrl(newVouchers);
    },
    [appliedVouchersState],
  );

  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  // Blocks forward navigation when the pending-booking session cannot be verified
  // (see the FlowBlock type): terminal "expired" lock vs recoverable "transient".
  const [flowBlock, setFlowBlock] = useState<FlowBlock | null>(null);
  /**
   * Whether an offer has ever been part of this journey — supplied in the URL,
   * chosen by the guest, or merely shown to them.
   *
   * Monotonic on purpose. `updateBooking` writes the offer too, so the pending
   * booking can hold one the guest can no longer see (they picked an offer,
   * went back, and chose details it is not available for). Reconciliation on
   * the details step must not be skipped just because the offer list has since
   * emptied and the local selection has been reset.
   */
  const offersEverInPlayRef = useRef(Boolean(queryParams.offer));
  const detailsFormRef = useRef<HTMLDivElement>(null);

  /**
   * Wrapper around setStage that also pushes a browser history entry.
   * Used for forward navigation so the browser back button works.
   *
   * `isEnquiry` is applied to the wizard state as well as the history entry, so
   * a caller cannot record one without the other. It is only ever set here,
   * never cleared, so a later forward navigation cannot downgrade an enquiry
   * back to a booking.
   */
  const navigateToStage = useCallback(
    (stage: z.infer<typeof historyStateSchema>["stage"], isEnquiry?: boolean) => {
      // Leaving the first stage forwards means a return to it is a step back, to
      // the sub-step the guest left from; going to it is a fresh start on its own
      // first sub-step ("Change details" from the booking summary).
      setResumeInitialAtTime(stage !== "initial");
      if (stage !== "initial") setHasReachedLaterStage(true);
      window.history.pushState({ stage, isEnquiry }, "");
      if (isEnquiry) {
        setIsEnquiry(true);
      }
      setStage(stage);
      // Forward navigation leaves the stage where any guard failure occurred, so
      // clear any pending block to keep it from lingering on the next stage.
      setFlowBlock(null);
      clearError();
      clearSubmitError();
      clearValidationErrors();
    },
    [setStage, setIsEnquiry, clearError, clearSubmitError, clearValidationErrors],
  );

  /**
   * Verify the pending booking is still valid before running `proceed` (which
   * advances the guest to pre-orders / deposit / payment) — mirrors DMN's
   * `verifyPendingBooking` guard in showPreordersSection() / showDepositSection()
   * / confirmPreorders().
   *
   * On success it runs `proceed`. On a genuine session expiry it locks the flow
   * (terminal) so the guest cannot reach payment for a booking the server would
   * reject. On a transient failure (network blip, timeout, upstream 5xx) it shows
   * a recoverable retry notice instead of forcing a full restart. Bookings with
   * no pending id (nothing to verify) proceed, matching DMN's else branch.
   */
  const runWithSessionGuard = useCallback(
    async (proceed: () => void | Promise<void>): Promise<void> => {
      if (!state.pendingBookingId) {
        await proceed();
        return;
      }

      let result: { valid: boolean; message?: string; retryable?: boolean };
      try {
        result = await call("/verifyPendingBooking", { pending_booking_id: state.pendingBookingId });
      } catch (err) {
        if (isAbortedRequest(err)) return;
        // Couldn't even reach our own server — transient, allow a retry.
        console.error("[verifyPendingBooking] Verification request failed:", err);
        setFlowBlock({ kind: "transient", message: null, proceed });
        return;
      }

      if (result.valid) {
        setFlowBlock(null);
        await proceed();
        return;
      }
      if (result.retryable) {
        setFlowBlock({ kind: "transient", message: result.message ?? null, proceed });
        return;
      }
      setFlowBlock({ kind: "expired", message: result.message ?? null });
    },
    [call, state.pendingBookingId],
  );

  /**
   * On mount, tag the initial history entry with the current stage
   * and listen for popstate events to handle browser back/forward.
   */
  useRequestScreen(state.stage);
  useEffect(() => {
    window.history.replaceState({ stage: state.stage, isEnquiry: state.isEnquiry }, "");
    const onPopState = (e: PopStateEvent) => {
      const parsed = historyStateSchema.safeParse(e.state);
      if (parsed.success) {
        // Booking and payment requests can't be cancelled, so keep the guest on this stage
        // until they settle, matching the disabled Back buttons.
        if (isBackLocked && parsed.data.stage !== state.stage) {
          window.history.pushState({ stage: state.stage, isEnquiry: state.isEnquiry }, "");
          return;
        }
        // Leaving the stage drops its outstanding requests. The server may still
        // apply them, so stages reload their basket from the server when re-entered.
        if (parsed.data.stage !== state.stage) abortScreen?.(state.stage);
        if (parsed.data.stage === "initial" && state.stage !== "initial") setResumeInitialAtTime(true);
        if (parsed.data.stage !== "initial" || state.stage !== "initial") setHasReachedLaterStage(true);
        setStage(parsed.data.stage);
        setIsEnquiry(parsed.data.isEnquiry ?? false);
        // Dismiss any pending verification block: navigating away from the stage
        // where the guard failed must clear the notice so it cannot persist on an
        // unrelated stage or run a stale `proceed` closure on retry.
        setFlowBlock(null);
        clearError();
        clearSubmitError();
        clearValidationErrors();
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [
    abortScreen,
    isBackLocked,
    setStage,
    setIsEnquiry,
    state.stage,
    state.isEnquiry,
    clearError,
    clearSubmitError,
    clearValidationErrors,
  ]);

  /**
   * Flatten API validation errors from the errors map into a list of messages.
   * The API returns errors as { error_code: ["message1", "message2"] }.
   */
  const validationErrorMessages = useMemo(() => getValidationErrorMessages(state.errors), [state.errors]);

  // Seed URL query parameters into booking state and auto-skip the
  // initial stage when all required params are present.
  useUrlParamSeeding({
    queryParams,
    venueConfigLoaded: !!state.venueConfig,
    loading: state.loading,
    error: state.error,
    errors: state.errors,
    currentBookingDetails: state.coreBookingDetails,
    availability: state.availability,
    updateBooking,
    updateCoreDetails,
    navigateToStage,
  });

  // Reverse-redirect: if the venue does NOT have new_booking_experience enabled,
  // redirect back to the old DMN booking page.
  const isRedirecting = useOldExperienceRedirect(state.venueConfig, queryParams.venue_id);

  useEffect(() => {
    if (selectedOffer !== null || state.offers.length > 0) {
      offersEverInPlayRef.current = true;
    }
  }, [selectedOffer, state.offers]);

  /** Reset selectedOffer when offers change (e.g. user changes booking details).
   *  Preserve the selection if the current offer still exists in the new list
   *  (e.g. URL-provided offer surviving initial and auto-skip API calls). */
  usePreservedSelection(state.offers, setSelectedOffer, byUnderscoreId);

  /** Reset selectedPackage when packages change (e.g. user changes booking details).
   *  Preserve the selection if the current package still exists in the new list. */
  usePreservedSelection(state.packages, setSelectedPackage, byId);

  /** Focus the error banner when a create/submit error appears. */
  useEffect(() => {
    if (state.error ?? state.submitError ?? state.submitUnconfirmed) {
      errorBannerRef.current?.focus();
    }
  }, [state.error, state.submitError, state.submitUnconfirmed]);

  /** Populate confirmationData from the /submitBooking response when it arrives. */
  useEffect(() => {
    const result = state.submitResult;
    if (!result) return;

    const booking = result.booking;
    setConfirmationData({
      booking: {
        _id: booking?._id ?? "",
        reference: booking?.reference ?? "",
        venue_id: booking?.venue_id ?? state.coreBookingDetails.venue_id ?? queryParams.venue_id,
        date: booking?.date ?? state.coreBookingDetails.date ?? "",
        time: booking?.time ?? state.coreBookingDetails.time ?? "",
        num_people: booking?.num_people ?? state.coreBookingDetails.num_people ?? 0,
        type: booking?.type ?? { id: state.coreBookingDetails.type ?? "", name: "" },
        first_name: booking?.first_name ?? "",
        last_name: booking?.last_name ?? "",
        email: booking?.email ?? "",
        deposits: booking?.deposits ?? [],
      },
      bookingStatus: result.bookingStatus === "confirmed" ? "confirmed" : "received",
      additionalBookings: result.additionalBookings ?? [],
      venue: {
        _id: result.venue._id,
        title: result.venue.title,
        path: result.venue.path ?? "",
        description: "",
        currency: result.venue.currency ?? "GBP",
        country_code: state.countryConfig?.country_code,
      },
    });
  }, [state.submitResult, queryParams.venue_id, state.coreBookingDetails, state.countryConfig]);

  /**
   * Determine the next stage after customer details are confirmed (Section 8.2).
   *
   * - If preorders exist for the selected booking type -> 'preorders'
   * - If deposit required and availability accepted -> 'deposit'
   * - Otherwise -> submit directly -> 'complete' or 'complete-redirect'
   */
  const determineNextStage = useCallback((): BookingStage => {
    const selectedTypeId = state.coreBookingDetails.type;
    const preorders = state.bookingOptions?.preorders;

    const typeMenus = selectedTypeId && preorders ? preorders[selectedTypeId] : undefined;
    const hasPreorders = !!(typeMenus && typeMenus.length > 0);

    if (hasPreorders) {
      return "preorders";
    }

    const hasDeposit = !!state.bookingOptions?.depositRequired;
    const isAccepted = state.availability?.action === "accept";

    if (hasDeposit && isAccepted) {
      return "deposit";
    }

    // No preorders and no deposit — submit directly
    return queryParams.return_url ? "complete-redirect" : "complete";
  }, [state.bookingOptions, state.availability, state.coreBookingDetails.type, queryParams.return_url]);

  /**
   * Build a BookingSubmitRequest from customer form data + booking state.
   */
  const buildSubmitRequest = useCallback(
    (
      formData: CustomerFormSubmitData,
      preorderSelectionsOverride?: PreorderSelections | null,
      paymentDetails?: unknown,
    ): BookingSubmitRequest => {
      const effectivePreorderSelections = preorderSelectionsOverride ?? preorderSelections;
      const selectedType = state.venueConfig?.bookingTypes.find((bt) => bt.id === state.coreBookingDetails.type);
      // Use resolvedBookingType as fallback when the venueConfig lookup fails.
      // This handles cross-sell scenarios where the booking type ID from the
      // original venue does not exist in the cross-sell venue's bookingTypes.
      const resolvedType = state.resolvedBookingType;

      // Transform tags from Record<id, value> to the array format expected by the API
      const tagsArray = transformTagsToArray(formData.tags, state.venueConfig?.bookingTags);

      // Build preorder payload from selections when available
      const customerNotes = dietaryRequirementsToCustomerNotes(effectivePreorderSelections?.dietaryRequirements);
      const hasPreorderItems = (effectivePreorderSelections?.rawItems?.length ?? 0) > 0;
      const preorder =
        hasPreorderItems && state.coreBookingDetails.type
          ? {
              [state.coreBookingDetails.type]: {
                items: effectivePreorderSelections?.rawItems ?? [],
                serviceChargePercentage: effectivePreorderSelections?.serviceChargePercentage,
                serviceChargeAmount: effectivePreorderSelections?.serviceChargeAmount,
                pendingPreorderId: effectivePreorderSelections?.pendingPreorderId,
                ...(customerNotes ? { customer_notes: customerNotes } : {}),
                ...(effectivePreorderSelections?.dietaryRequirements?.length
                  ? { dietaryRequirements: withoutDietaryNotes(effectivePreorderSelections.dietaryRequirements) }
                  : {}),
              },
            }
          : undefined;

      return {
        additional_types: JSON.stringify(state.bookingOptions?.additionalTypes ?? []),
        pending_booking_id: state.pendingBookingId ?? "",
        source: queryParams.source,
        tags: tagsArray,
        venue_id: state.coreBookingDetails.venue_id ?? queryParams.venue_id,
        venue_group: state.venueConfig?.venueGroup ?? "",
        assigned_areas: [],
        region: state.venueConfig?.region ?? "",
        date: state.coreBookingDetails.date ?? "",
        time: state.coreBookingDetails.time ?? "",
        duration: state.coreBookingDetails.duration ?? undefined,
        num_people: state.coreBookingDetails.num_people ?? 0,
        type: {
          guestlist: selectedType?.guestlist ?? resolvedType?.guestlist ?? false,
          id: state.coreBookingDetails.type ?? "",
          name: selectedType?.name ?? resolvedType?.name ?? "",
          value: selectedType?.value ?? resolvedType?.value ?? 0,
          private_hire: selectedType?.private_hire ?? resolvedType?.private_hire ?? false,
          use_standing_capacity: selectedType?.use_standing_capacity ?? resolvedType?.use_standing_capacity ?? false,
        },
        payments: paymentDetails ? [paymentDetails] : [],
        deposit: paymentDetails ? { payment_model: paymentDetails } : undefined,
        newsletter_signup: formData.newsletter_signup,
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        notes: formData.notes || undefined,
        phone: formData.phone,
        policy_confirm: formData.policy_confirm,
        marketing_preferences: formData.marketing_preferences,
        accessToken: state.accessToken ?? "",
        dob: formData.dob,
        partner_data_permission: formData.partner_data_permission,
        group_members: formData.group_members.length > 0 ? formData.group_members.map((m) => ({ ...m })) : undefined,
        preorder,
        do_enquiry: state.isEnquiry || undefined,
        // A declined offer must be sent as a blank rather than omitted. Collins
        // builds the booking from the pending booking and `updateFromArray`
        // skips keys the payload does not carry, so leaving it out preserves
        // whatever is stored — the `?offer=` param written at `/book`, or an
        // offer a details update saved before the guest could no longer see it.
        // A blank offer is how Collins unsets one.
        offer: selectedOffer ?? (offersEverInPlayRef.current ? "" : undefined),
        package: selectedPackage ?? undefined,
        affiliate_id: queryParams.affiliate_id ?? undefined,
        locale: queryParams.locale ?? undefined,
        affiliate_data_permission: formData.affiliate_data_permission,
        custom_field_value: formData.custom_field_value,
        custom_field_label: formData.custom_field_label,
        vouchers: appliedVouchersState.length > 0 ? appliedVouchersState.map((voucher) => voucher.code) : undefined,
      };
    },
    [state, queryParams, preorderSelections, selectedOffer, selectedPackage, appliedVouchersState],
  );

  /**
   * Handle "Continue" from the customer details stage.
   * Builds a BookingSubmitRequest from form data + booking state,
   * submits directly if no preorders/deposit are needed, then
   * advances to the determined next stage.
   *
   * Customer data is always saved so it can be used later when the
   * preorder or payment step completes and triggers submission.
   */
  /**
   * Bind the partner return target before the guest can pay. A Stripe payment
   * confirms the booking on /confirm, which redirects only to the target bound
   * here — so it must be in place before payment starts, and the guest is asked
   * to retry rather than lose the return to the partner.
   *
   * Resolves `false` when the guest cannot go on yet, so the caller stops.
   */
  const bindReturnTargetBeforePayment = useCallback(
    async (retry: () => Promise<void>): Promise<boolean> => {
      const returnBinding = partnerReturnBinding(queryParams.return_url, queryParams.return_method);
      if (!returnBinding || !state.pendingBookingId) return true;

      try {
        await call("/bindReturnUrl", { pending_booking_id: state.pendingBookingId, ...returnBinding });
        return true;
      } catch (err) {
        if (isAbortedRequest(err)) return false;
        console.error("[BookingForm] Failed to bind the partner return url:", err);
        setFlowBlock({
          kind: "transient",
          message: t(
            "booking_return_bind_failed",
            "We couldn't prepare your booking for payment just now. Please check your connection and try again.",
          ),
          proceed: retry,
        });
        return false;
      }
    },
    [call, queryParams.return_url, queryParams.return_method, state.pendingBookingId, t],
  );

  const handleCustomerDetailsContinue = useCallback(
    async (formData: CustomerFormSubmitData) => {
      const nextStage = determineNextStage();
      setSavedCustomerData(formData);

      if (nextStage === "complete" || nextStage === "complete-redirect") {
        const submitRequest = buildSubmitRequest(formData);
        setLastSubmitData(submitRequest);
        try {
          await submitBooking(submitRequest);
          window.history.replaceState({ stage: nextStage }, "");
          setStage(nextStage);
        } catch {
          // Stay on the current stage — the submitError banner handles display.
        }
        return;
      }

      // Guard the pre-order / deposit transitions: verify the pending booking is
      // still valid before advancing. A genuine session expiry locks the flow;
      // a transient failure offers a retry — both stop the guest proceeding (and
      // potentially paying) for a booking the server would reject.
      // Reconciling the offer, binding the partner return target, updating the
      // customer details and navigating are one step, so `proceed` re-runs them
      // all when the guest retries.
      const proceed = async (): Promise<void> => {
        // Write the guest's offer choice to the pending booking before they can
        // be sent to pay. `/submitBooking` — the only other carrier of the offer —
        // never runs when a Stripe Connect deposit or paid pre-order creates the
        // booking server-side from the pending booking after the payment redirect,
        // so an offer held only in browser state would be lost.
        //
        // Written unconditionally rather than only when the local selection has
        // moved: `updateBooking` writes the offer as well, so the stored value
        // can differ from anything this component last sent.
        //
        // Blocking, unlike the customer-details update below: the guest is about
        // to pay, and taking their money for a booking that silently lacks the
        // offer they chose — or carries one they declined — is worse than asking
        // them to try again.
        if (offersEverInPlayRef.current) {
          try {
            await persistOfferToPendingBooking(selectedOffer);
          } catch (err) {
            if (isAbortedRequest(err)) return;
            console.error("[BookingForm] Failed to persist the selected offer to the pending booking:", err);
            setFlowBlock({
              kind: "transient",
              message: t(
                "booking_offer_save_failed",
                "We couldn't save your offer selection just now. Please check your connection and try again.",
              ),
              proceed,
            });
            return;
          }
        }

        if (!(await bindReturnTargetBeforePayment(proceed))) return;

        // Fire-and-forget: update customer details on the pending booking via Collins API.
        // Only fires when proceeding to preorders/deposit — not when submitting directly,
        // since the submit request already carries all customer data.
        if (state.pendingBookingId) {
          call("/updateBookingCustomer", {
            pending_booking_id: state.pendingBookingId,
            email: formData.email,
            first_name: formData.first_name,
            last_name: formData.last_name,
            phone: formData.phone,
            notes: formData.notes ? formData.notes : undefined,
            dob: formData.dob ? formData.dob : undefined,
            policy_confirm: formData.policy_confirm,
            newsletter_signup: formData.newsletter_signup,
            partner_data_permission: formData.partner_data_permission,
            marketing_preferences: formData.marketing_preferences,
            tags: transformTagsToArray(formData.tags, state.venueConfig?.bookingTags),
            group_members:
              formData.group_members.length > 0 ? formData.group_members.map((m) => ({ ...m })) : undefined,
          }).catch((err) => {
            if (isAbortedRequest(err)) return;
            console.error("[updateBookingCustomer] Failed to update customer details:", err);
          });
        }

        navigateToStage(nextStage);
      };

      await runWithSessionGuard(proceed);
    },
    [
      call,
      determineNextStage,
      navigateToStage,
      submitBooking,
      buildSubmitRequest,
      setStage,
      runWithSessionGuard,
      selectedOffer,
      persistOfferToPendingBooking,
      state.pendingBookingId,
      state.venueConfig?.bookingTags,
      bindReturnTargetBeforePayment,
      t,
    ],
  );

  /**
   * Submit the booking using saved customer data.
   * Called from the preorder or payment step once those stages complete.
   */
  const submitFromSavedData = useCallback(
    async (preorderSelectionsOverride?: PreorderSelections | null, paymentDetails?: unknown) => {
      if (!savedCustomerData) return;

      const effectiveSelections = preorderSelectionsOverride ?? preorderSelections;
      const hasPreorderItems = (effectiveSelections?.rawItems?.length ?? 0) > 0;

      const noPaymentMade = paymentDetails === undefined;
      if (hasPreorderItems && state.pendingBookingId && noPaymentMade) {
        try {
          const cacheStatus = await call("/getPreorderCacheStatus", { booking_id: state.pendingBookingId });
          if (!cacheStatus.active) {
            setPreorderSelections(null);
            setPreorderCacheExpired(true);
            setStage("preorders");
            return;
          }
        } catch {
          // Network error checking cache — allow submission to proceed rather
          // than blocking. The server-side submit will reject if the basket is
          // truly gone.
        }
      }

      const targetStage = queryParams.return_url ? "complete-redirect" : "complete";
      const submitRequest = buildSubmitRequest(savedCustomerData, preorderSelectionsOverride, paymentDetails);
      setLastSubmitData(submitRequest);
      try {
        await submitBooking(submitRequest);
        window.history.replaceState({ stage: targetStage }, "");
        setStage(targetStage);
      } catch {
        // Stay on the current stage — the submitError banner handles display.
      }
    },
    [
      call,
      savedCustomerData,
      queryParams.return_url,
      buildSubmitRequest,
      submitBooking,
      setStage,
      preorderSelections,
      state.pendingBookingId,
    ],
  );

  /**
   * Handle completion of the pre-order stage.
   *
   * Verifies the pending booking is still valid before confirming the pre-order
   * and moving to payment — mirrors DMN's confirmPreorders(). Lifted out of the
   * render path into a stable callback to keep function nesting shallow (S2004).
   */
  const handlePreorderComplete = useCallback(
    async (result: PreorderCompleteResult) => {
      await runWithSessionGuard(async () => {
        // Carry the booking type's pre-order payment requirement onto the
        // selections so PaymentStep can tell a bundled-but-payable pre-order
        // (required payment reported as 0) apart from a pay-at-venue one.
        const selectedType = state.venueConfig?.bookingTypes.find((bt) => bt.id === state.coreBookingDetails.type);
        const selections: PreorderSelections = {
          ...buildPreorderSelections(result),
          preorderPaymentRequired: selectedType?.customer_preorder_settings?.payment_required ?? null,
        };
        setPreorderSelections(selections);
        setPreorderCacheExpired(false);

        // Sync preorder + service charge to the pending booking before payment.
        // `booking_type` must match the key the pre-order menu uses when caching
        // its basket (the selected booking type), otherwise the service charge
        // update is cached under a separate "default" entry. That duplicate entry
        // makes the basket later treat the order as having multiple booking types
        // and aggregate the items from both, doubling the basket on back navigation.
        // Linked bookings: skip service charge recalculation
        const isLinkedBooking =
          Array.isArray(state.bookingOptions?.additionalTypes) && state.bookingOptions.additionalTypes.length > 0;
        if (state.pendingBookingId && !isLinkedBooking) {
          try {
            await call("/updatePreOrderServiceCharge", {
              booking_id: state.pendingBookingId,
              pendingPreorderId: result.pendingPreorderId ?? null,
              items: result.items,
              booking: { _id: state.pendingBookingId, is_pending: true },
              serviceChargePercentage: result.serviceChargePercentage,
              bookingType: state.coreBookingDetails.type ?? undefined,
              booking_type: state.coreBookingDetails.type ?? undefined,
            });
          } catch (err) {
            if (isAbortedRequest(err)) return;
            console.error("[BookingForm] Failed to recalculate pending booking:", err);
          }
        }

        const hasDeposit = !!state.bookingOptions?.depositRequired;
        const isAccepted = state.availability?.action === "accept";
        const hasPreorderPayment = selections.requiredPaymentAmount > 0;

        // Navigate to payment if:
        // 1. Has preorder payment (items in basket), OR
        // 2. Has deposit and availability is accepted (even with empty basket)
        if (hasPreorderPayment || (hasDeposit && isAccepted)) {
          navigateToStage("payment");
        } else {
          await submitFromSavedData(selections);
        }
      });
    },
    [
      call,
      runWithSessionGuard,
      submitFromSavedData,
      navigateToStage,
      state.bookingOptions,
      state.pendingBookingId,
      state.coreBookingDetails.type,
      state.availability,
      state.venueConfig,
    ],
  );

  /**
   * Retry the /submitBooking call using the last submitted data.
   * Used when a network error occurs on submit.
   */
  const handleRetrySubmit = useCallback(async () => {
    if (!lastSubmitData) return;
    clearSubmitError();
    const targetStage = queryParams.return_url ? "complete-redirect" : "complete";
    try {
      await submitBooking(lastSubmitData);
      window.history.replaceState({ stage: targetStage }, "");
      setStage(targetStage);
    } catch {
      // Stay on the current stage — the submitError banner handles display.
    }
  }, [lastSubmitData, clearSubmitError, submitBooking, queryParams.return_url, setStage]);

  /**
   * Handle preorder cache expiry at the BookingForm level.
   *
   * This fires when `PreorderExpiryWarning` (rendered below across preorder and
   * payment stages) detects the Redis basket has expired. Clears saved
   * selections so the guest cannot pay for items the server no longer holds, and
   * navigates back to the preorders stage where the expiry banner will be shown.
   */
  const handlePreorderCacheExpiredAtFormLevel = useCallback(() => {
    setPreorderSelections(null);
    setPreorderCacheExpired(true);
    if (state.stage === "payment") {
      setStage("preorders");
    }
  }, [state.stage, setStage]);

  const handlePreorderCacheExtended = useCallback(() => {
    setPreorderExpiryResyncKey((key) => key + 1);
  }, []);

  /**
   * Extract preorder menus for the selected booking type from booking options.
   * The API returns preorders keyed by booking type ID.
   */
  const selectedTypeId = state.coreBookingDetails.type;

  const { preorderMenus, linkedPreorderMenus } = useMemo(() => {
    const preordersMap = state.bookingOptions?.preorders;
    const menus = selectedTypeId && preordersMap ? (preordersMap[selectedTypeId] ?? []) : [];
    const linked = findLinkedBookingTypes(state.bookingOptions, state.venueConfig?.bookingTypes);
    const linkedMenus =
      preordersMap && linked.length > 0
        ? linked.map((linkedType) => ({
            bookingTypeId: linkedType.id,
            bookingTypeName: linkedType.name,
            menus: preordersMap[linkedType.id] ?? [],
          }))
        : null;
    return { preorderMenus: menus, linkedPreorderMenus: linkedMenus };
  }, [selectedTypeId, state.bookingOptions, state.venueConfig?.bookingTypes]);
  useWidgetAutoExpandForPreorder(state.stage, preorderMenus.length > 0);

  /**
   * Construct a PendingBooking object from the hook state
   * for child components that expect it.
   */
  const { pendingBooking, effectiveBookingType, serviceChargePercentage, disableShareableLink } = useMemo(() => {
    /** Selected booking type object for sidebar display and config lookups. */
    const selectedBookingType = state.venueConfig?.bookingTypes.find((bt) => bt.id === state.coreBookingDetails.type);
    return {
      pendingBooking: state.pendingBookingId
        ? {
            _id: state.pendingBookingId,
            venue_id: state.coreBookingDetails.venue_id ?? queryParams.venue_id,
            date: state.coreBookingDetails.date ?? "",
            time: state.coreBookingDetails.time ?? "",
            num_people: state.coreBookingDetails.num_people ?? 0,
            duration: state.coreBookingDetails.duration ?? 0,
            type: {
              id: state.coreBookingDetails.type ?? "",
              name: selectedBookingType?.name ?? state.resolvedBookingType?.name ?? "",
            },
            is_pending: true,
          }
        : null,
      effectiveBookingType: selectedBookingType ?? state.resolvedBookingType,
      serviceChargePercentage: selectedBookingType?.customer_preorder_settings?.service_charge_percentage ?? 0,
      disableShareableLink: selectedBookingType?.customer_preorder_settings?.disable_shareable_link ?? false,
    };
  }, [
    state.venueConfig?.bookingTypes,
    state.coreBookingDetails,
    state.resolvedBookingType,
    state.pendingBookingId,
    queryParams.venue_id,
  ]);

  /** Payment processor sourced from the booking data via /getBooking RPC. */
  const paymentProcessor = state.paymentProcessor ?? "";

  /** Current booking journey stage. */
  const currentStage = state.stage;

  /** Country code from venue/country config for formatting. */
  const countryCode = state.countryConfig?.country_code ?? "GB";

  /**
   * Scroll to the top of the page on stage transitions. Inside the widget the
   * embedding page is scrolled to the journey's saved position instead, and not
   * on the first stage, so mounting the widget leaves the host page alone.
   */
  const previousStageRef = useRef<typeof currentStage | undefined>(undefined);
  useEffect(() => {
    if (!currentStage) {
      return;
    }

    const previousStage = previousStageRef.current;
    if (previousStage === currentStage) {
      return;
    }
    previousStageRef.current = currentStage;

    if (scrollWidgetToScreenTop) {
      if (previousStage !== undefined) {
        scrollWidgetToScreenTop();
      }
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStage, scrollWidgetToScreenTop]);

  /** The sidebar is visible on initial and details stages once venue config has loaded. */
  const sidebarVisible = !!state.venueConfig;

  /** Whether the sidebar should render — only on initial and details stages. */
  const showSidebar = sidebarVisible && (state.stage === "initial" || state.stage === "details");

  const { showStepHeader, showInlineBookingInfo } = getStageVisibility(state.stage);

  const { bookingSteps } = useBookingSteps({
    selectedTypeId: state.coreBookingDetails.type,
    bookingOptions: state.bookingOptions,
    availability: state.availability,
  });

  const { currentStepInfo, currentStepNumber, totalStepCount } = getStepIndicatorInfo(bookingSteps, state.stage);

  /**
   * Whether the customer details step is the final step before submission.
   * When a pre-order or payment step follows, the submit button reads "Next".
   */
  const detailsIsFinalStep = (() => {
    const nextStage = determineNextStage();
    return nextStage === "complete" || nextStage === "complete-redirect";
  })();

  const { formattedDate, formattedTime, formattedCovers } = useFormattedBookingInfo({
    coreBookingDetails: state.coreBookingDetails,
    countryCode,
  });

  /**
   * Mobile step nav rendered inside the BookingHeader on small screens. The widget
   * has no use for it: its top bar carries Back at every viewport width.
   */
  const mobileStepNav =
    showStepHeader && !widgetTopBarSlot ? (
      <MobileStepNav
        currentStepInfo={currentStepInfo}
        currentStepNumber={currentStepNumber}
        totalStepCount={totalStepCount}
      />
    ) : null;

  const handleNavigateToDetails = useCallback(() => navigateToStage("details"), [navigateToStage]);
  const handleEnquiry = useCallback(() => {
    setIsEnquiry(true);
    navigateToStage("details", true);
  }, [setIsEnquiry, navigateToStage]);
  const handleScrollToDetails = useCallback(() => detailsFormRef.current?.scrollIntoView({ behavior: "smooth" }), []);
  const handleAccountLinked = useCallback(() => {
    updateBooking({}).catch((err) => {
      if (isAbortedRequest(err)) return;
      console.error("[BookingForm] Failed to update booking after account link:", err);
    });
  }, [updateBooking]);
  const handlePaymentComplete = useCallback(
    (paymentDetails: unknown) => submitFromSavedData(preorderSelections, paymentDetails),
    [submitFromSavedData, preorderSelections],
  );
  const handleHistoryBack = useCallback(() => {
    if (!isBackLocked) window.history.back();
  }, [isBackLocked]);
  const {
    initialWizardStep,
    detailsOffers,
    presetCoversValue,
    paymentStage,
    showWidgetStageHeading,
    confirmationSpecialRequest,
    requestedPackageLabel,
    selectedOfferTitle,
  } = useMemo(
    () => ({
      initialWizardStep: widgetTopBarSlot && resumeInitialAtTime ? ("time" as const) : undefined,
      detailsOffers: queryParams.hide_offers ? [] : state.offers,
      presetCoversValue: settings.isWidget ? WIDGET_PRESET_COVERS : undefined,
      paymentStage: state.stage === "payment" ? ("payment" as const) : ("deposit" as const),
      showWidgetStageHeading:
        !!widgetTopBarSlot && (state.stage === "payment" || state.stage === "deposit" || state.stage === "preorders"),
      confirmationSpecialRequest: savedCustomerData?.notes || undefined,
      requestedPackageLabel: selectedPackage ? state.packages.find((p) => p.id === selectedPackage)?.label : undefined,
      selectedOfferTitle: selectedOffer ? state.offers.find((o) => o._id === selectedOffer)?.title : undefined,
    }),
    [
      widgetTopBarSlot,
      resumeInitialAtTime,
      queryParams.hide_offers,
      state.offers,
      settings.isWidget,
      state.stage,
      savedCustomerData?.notes,
      selectedPackage,
      state.packages,
      selectedOffer,
    ],
  );

  const finalDepositRequired = state.bookingOptions?.depositRequired;

  const renderStage = (): React.ReactNode => {
    switch (state.stage) {
      case "initial":
        return (
          <div ref={detailsFormRef}>
            <BookingDetailsForm
              key={selectedVenueId}
              venueId={selectedVenueId}
              source={queryParams.source}
              initialDate={state.coreBookingDetails.date ?? queryParams.date}
              initialTime={initialTime}
              initialDuration={initialDuration}
              initialCovers={state.coreBookingDetails.num_people ?? queryParams.num_people}
              initialType={state.coreBookingDetails.type ?? queryParams.type}
              fixedType={queryParams.fixed_type}
              venueConfig={state.venueConfig}
              availability={state.availability}
              bookingOptions={state.bookingOptions}
              timeOptions={state.timeOptions}
              durationOptions={state.durationOptions}
              loading={state.loading}
              errors={state.errors}
              countryCode={countryCode}
              showTypeFirst={queryParams.show_type_first}
              updateBooking={updateBooking}
              updateCoreDetails={updateCoreDetails}
              onNext={handleNavigateToDetails}
              onAccept={handleNavigateToDetails}
              onEnquiry={handleEnquiry}
              initialWizardStep={initialWizardStep}
              resumeFromLaterStage={initialWizardStep === "time"}
              onChangeDetails={handleScrollToDetails}
              resolvedBookingTypeName={state.resolvedBookingType?.name}
              bookingError={state.error}
              onClearValidationErrors={clearValidationErrors}
              rewards={hasReachedLaterStage ? null : state.rewards}
              affiliateId={queryParams.affiliate_id}
              isMultiVenue={isMultiVenue}
              venueList={venueList}
              venueListLoading={venueListLoading}
              venueListError={venueListError}
              onRetryVenueList={retryVenueList}
              selectedVenueId={selectedVenueId}
              onVenueSelect={handleVenueSelect}
              searchVenues={queryParams.search_venues}
              allowedTypes={settings.allowedTypes}
              loggedInUser={state.pendingBookingUser}
              bookingTypeName={state.resolvedBookingType?.name}
              presetCovers={presetCoversValue}
            />
          </div>
        );

      case "details":
        return (
          <CustomerDetailsForm
            initialValues={savedCustomerFormValues}
            prefill={prefill}
            onSaveFormState={setSavedCustomerFormValues}
            bookingOptions={state.bookingOptions}
            venueConfig={state.venueConfig}
            countryConfig={state.countryConfig}
            source={queryParams.source}
            numPeople={state.coreBookingDetails.num_people ?? 1}
            selectedBookingTypeId={state.coreBookingDetails.type}
            onSubmit={handleCustomerDetailsContinue}
            initialLoggedInUser={state.pendingBookingUser}
            rewards={state.rewards}
            affiliateId={queryParams.affiliate_id}
            bookingTypeName={state.resolvedBookingType?.name}
            isEnquiry={state.isEnquiry}
            bookingDate={state.coreBookingDetails.date}
            bookingTime={state.coreBookingDetails.time}
            isFinalStep={detailsIsFinalStep}
            offers={detailsOffers}
            selectedOffer={selectedOffer}
            onSelectOffer={setSelectedOffer}
            packages={state.packages}
            selectedPackage={selectedPackage}
            onSelectPackage={setSelectedPackage}
            affiliate={state.affiliate}
            locale={queryParams.locale}
            pendingBookingId={state.pendingBookingId}
            onAccountLinked={handleAccountLinked}
            onAccountUnlinked={clearPendingBookingUser}
            submitHidden={state.submitUnconfirmed || state.paymentUnconfirmed}
          />
        );

      case "preorders":
        return (
          <BookPreorderStage
            pendingBooking={pendingBooking}
            preorderMenus={preorderMenus}
            linkedBookings={linkedPreorderMenus}
            venueConfig={state.venueConfig ?? null}
            countryConfig={state.countryConfig ?? null}
            serviceChargePercentage={serviceChargePercentage}
            hideDietaryRequirements={state.hideDietaryRequirements}
            disableShareableLink={disableShareableLink}
            depositRequired={finalDepositRequired ?? undefined}
            preorderSelections={preorderSelections}
            onBack={handleHistoryBack}
            onCacheExpired={() => {
              setPreorderSelections(null);
              setPreorderCacheExpired(true);
            }}
            onCacheExtended={handlePreorderCacheExtended}
            onComplete={handlePreorderComplete}
            headingSlot={showWidgetStageHeading ? stageHeadingSlot : null}
            submitHidden={state.submitUnconfirmed || state.paymentUnconfirmed}
          />
        );

      case "payment":
      case "deposit":
        return (
          <PaymentStep
            bookingOptions={state.bookingOptions ?? null}
            venueConfig={state.venueConfig ?? null}
            stage={paymentStage}
            pendingBookingId={state.pendingBookingId}
            booking={pendingBooking}
            preorderSelections={preorderSelections}
            paymentProcessor={paymentProcessor}
            countryCode={countryCode}
            onBack={handleHistoryBack}
            onPaymentComplete={handlePaymentComplete}
            onPaymentUnconfirmed={setPaymentUnconfirmed}
            onVouchersChange={setAppliedVouchers}
            headingSlot={showWidgetStageHeading ? stageHeadingSlot : null}
            submitHidden={state.submitUnconfirmed || state.paymentUnconfirmed}
          />
        );

      case "complete":
      case "complete-redirect":
        return (
          <BookingConfirmation
            confirmationData={confirmationData}
            returnUrl={queryParams.return_url}
            returnMethod={queryParams.return_method}
            isRedirect={state.stage === "complete-redirect"}
            duration={state.coreBookingDetails.duration}
            rewards={state.rewards}
            source={queryParams.source}
            affiliateId={queryParams.affiliate_id}
            newsletterSignup={savedCustomerData?.newsletter_signup}
            isLoggedIn={savedCustomerData?.isLoggedIn}
            pointsBalance={state.pendingBookingUser?.points_balance}
            isEnquiry={state.isEnquiry}
            specialRequest={confirmationSpecialRequest}
            requestedPackageLabel={requestedPackageLabel}
            selectedOfferTitle={selectedOfferTitle}
            preorderSelections={preorderSelections}
            depositType={state.bookingOptions?.depositRequired?.type}
            paymentJourneyId={state.pendingBookingId ?? undefined}
          />
        );

      default:
        return null;
    }
  };

  if (isRedirecting) {
    return (
      <PageStateGuard loading={true} error={null} data={null}>
        {null}
      </PageStateGuard>
    );
  }

  if (queryParams.venue_ids.length === 0) {
    return (
      <PageStateGuard loading={false} error={t("booking_missing_venue_id")} data={null}>
        {null}
      </PageStateGuard>
    );
  }

  return (
    <Layout
      showHeader={isDmnHeaderSource(queryParams.source)}
      keepHeaderWhenHidden
      headerChildren={mobileStepNav}
      mainClassName="booking-form"
      main={
        <div id="gc-booking-flow" className={state.stage === "preorders" ? "" : "gcw:w-full gcw:p-6"}>
          {/* Screen reader announcements for stage transitions */}
          <div aria-live="polite" className="gcw:sr-only">
            {getStageAnnouncement(state.stage, state.isEnquiry, t)}
          </div>

          {/* Global error banner — createBooking network failure with retry */}
          {/* Submit error banner — /submitBooking network failure with retry */}
          {/* API validation errors banner — field-level messages from createBooking.
              Suppressed when all core booking fields are blank (noCoreDetails) to avoid
              showing errors before the user has entered anything — mirrors DMN's
              ng-if="errors && !noCoreDetails" from stage-initial.html. */}
          <BookingErrorBanners
            error={state.error}
            submitError={state.submitError}
            submitUnconfirmed={state.submitUnconfirmed}
            paymentUnconfirmed={state.paymentUnconfirmed}
            createBookingFailed={state.createBookingFailed}
            validationErrorMessages={validationErrorMessages}
            stage={state.stage}
            errorBannerRef={errorBannerRef}
            retryCreateBooking={retryCreateBooking}
            retrySubmit={handleRetrySubmit}
            t={t}
          />
          {/* Step indicator and Back control — desktop only (mobile uses header nav).
              In the widget the whole row gives way to the top bar's Back control, the one the
              earlier stages use, so every stage steps back the same way and from the same place. */}
          <BookingNavigation
            showStepHeader={showStepHeader}
            widgetTopBarSlot={widgetTopBarSlot}
            currentStage={state.stage}
            bookingSteps={bookingSteps}
            onBack={handleHistoryBack}
            backDisabled={isBackLocked}
            venueName={state.venueConfig?.title}
            showSidebar={showSidebar}
            t={t}
          />

          {showWidgetStageHeading && <div id="gc-stage-heading-slot" ref={setStageHeadingSlot} />}

          {/* Inline booking details — shown after customer details stage.
              On mobile includes venue name (since sidebar is hidden). */}
          {showInlineBookingInfo && (
            <BookingInfoInline
              venueName={state.venueConfig?.title}
              bookingDate={formattedDate}
              bookingTime={formattedTime}
              covers={formattedCovers}
              bookingTypeName={effectiveBookingType?.name}
              stage={state.stage}
              onChangeDetails={() => navigateToStage("initial")}
            />
          )}

          <PreorderExpiryMonitor
            stage={state.stage}
            preorderSelections={preorderSelections}
            preorderCacheExpired={preorderCacheExpired}
            pendingBookingId={state.pendingBookingId}
            resyncKey={preorderExpiryResyncKey}
            onExpired={handlePreorderCacheExpiredAtFormLevel}
          />

          <div id="gc-booking-stage-content">
            <GuardedStageContent flowBlock={flowBlock} runWithSessionGuard={runWithSessionGuard}>
              {renderStage()}
            </GuardedStageContent>
          </div>
        </div>
      }
      asideClassName={state.stage === "preorders" ? "bookingJourney basket-aside" : "bookingJourney"}
      venueInfo={
        !showSidebar ? undefined : (
          <div id="gc-booking-summary-sidebar">
            <BookingInfoSidebar
              venueTitle={state.venueConfig?.title}
              venueImageUrl={resolveVenueImageUrl(state.venueConfig)}
              bookingDate={formattedDate}
              bookingTime={formattedTime}
              covers={formattedCovers}
              bookingTypeName={effectiveBookingType?.name}
              onChangeDetails={() => navigateToStage("initial")}
              isComplete={sidebarVisible}
              minimal={state.stage === "initial"}
            />
          </div>
        )
      }
      aside={state.stage === "preorders" && <div className="basket" id="book-basket-portal"></div>}
    />
  );
};

export default BookingForm;
